import { Component, Inject, Input, OnInit, TemplateRef } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { from } from 'rxjs';
import { finalize } from 'rxjs/operators';
import { AlcmeonContextService } from 'src/app/core/alcmeon-context/alcmeon-context.service';
import { WhatsappCloudApiMessagingLimitEnum } from 'src/app/parameters/add-account/whatsapp-cloud-api/shared/models/whatsapp-cloud-api-messaging-limit.enum';
import { WhatsappCloudApiService } from 'src/app/parameters/add-account/whatsapp-cloud-api/shared/whatsapp-cloud-api.service';
import { WhatsappTemplatesService } from 'src/app/parameters/message-templates/whatsapp-templates/whatsapp-templates.service';
import { ConfirmDialogData } from 'src/app/shared/components/confirm-dialog/confirm-dialog-data.interface';
import { ConfirmDialogComponent } from 'src/app/shared/components/confirm-dialog/confirm-dialog.component';
import { WABAAssociation } from 'src/app/shared/components/select-whatsapp-waba/waba-association.model';
import { BasicService } from 'src/app/shared/services/basic.service';
import { ErrorHandlingService } from 'src/app/shared/services/error-handling.service';
import { WhatsappService } from 'src/app/shared/strategies/channels';
import { AccountsService } from '../../../accounts.service';
import { AccountInformation } from '../../../shared/models/account-information.model';
import { GenerateQrCodeComponent } from '../generate-qr-code/generate-qr-code.component';

@Component({
  selector: 'app-account-information',
  templateUrl: './account-information.component.html',
  styleUrls: ['./account-information.component.scss'],
})
export class AccountInformationComponent implements OnInit {
  @Input() account: AccountInformation;
  form: FormGroup = this.fb.group({
    name: ['', Validators.required],
    lang_code: ['', Validators.required],
    site_group_id: [0, Validators.required],
    enable_alert_email: [''],
  });
  languages = [];

  whatsappAccountType = this.whatsappService.getType();
  whatsappBusinessManagerLink;
  wabaAssociationsMap: Map<string, WABAAssociation> = new Map();
  isCloudAPI: boolean;
  whatsappCloudApiMessagingLimit: WhatsappCloudApiMessagingLimitEnum;
  WhatsappCloudApiMessagingLimitEnum = WhatsappCloudApiMessagingLimitEnum;
  whatsappCloudApiQualityScoreTooltips = {
    medium: this.basicService.translate('accounts/edition/quality-score/medium/tooltip'),
    low: this.basicService.translate('accounts/edition/quality-score/low/tooltip'),
  };
  whatsappCloudApiStatusTooltips = {
    flagged: this.basicService.translate('accounts/edition/status/flagged/tooltip'),
    connected: this.basicService.translate('accounts/edition/status/connected/tooltip'),
    restricted: this.basicService.translate('accounts/edition/status/restricted/tooltip'),
  };
  get whatsappCloudApiMessagingLimits() {
    return Object.keys(WhatsappCloudApiMessagingLimitEnum);
  }

  constructor(
    private fb: FormBuilder,
    public accountsService: AccountsService,
    private whatsappService: WhatsappService,
    private whatsappTemplatesService: WhatsappTemplatesService,
    private whatsappCloudApiService: WhatsappCloudApiService,
    private basicService: BasicService,
    private alcmeonContextService: AlcmeonContextService,
    private errorHandlingService: ErrorHandlingService,
    private dialog: MatDialog,
    @Inject('AppFactory') appService: any,
    @Inject('sites') private sites: any,
    @Inject('siteGroups') private siteGroups: any,
    @Inject('sitesMap') private sitesMap: any,
    @Inject('siteGroupsMap') private siteGroupsMap: any,
    @Inject('MediasFactory') private mediasService: any,
    @Inject('TopToolbarFactory') private topToolbarService: any,
    @Inject('$stateParams') private $stateParams: any
  ) {
    this.languages = appService.getAvailableLanguages().map(l => ({ id: l, label: this.basicService.translate(l) }));
  }

  ngOnInit(): void {
    if (this.accountsService.twitterSiteType === this.account.type) {
      this.form.addControl('default_reply_as', new FormControl('', Validators.required));
    }
    this.account = {
      ...this.account,
      site_group_id: this.account.site_group_id || this.account.site_group?.id || 0,
    };
    this.form.patchValue(this.account);
    this.accountsService.eligibleGroups = [{ name: this.basicService.translate('none'), id: 0 }].concat(
      this.siteGroups.filter(gr => gr.type === this.account.type)
    );
    if (this.whatsappAccountType === this.account.type) {
      const { waba_id, isCloudAPI, id } = this.account;
      this.getWABAAssociations(id);
      if (isCloudAPI) {
        this.isCloudAPI = isCloudAPI;
        this.getWhatsAppCloudAPIMessagingLimit();
      }
      this.whatsappBusinessManagerLink = `https://business.facebook.com/wa/manage/home/?business_id=241382819877621&waba_id=${waba_id}`;
    }
  }

  save(): void {
    const updated = (Object.keys(this.form.controls) || []).filter(controlName => this.form.get(controlName).dirty);
    const model = this.form.getRawValue();
    const payload = updated.reduce((_payload, key) => ({ ..._payload, [key]: model[key] }), {});
    this.accountsService.updateAccountInformation(this.account.id, payload).subscribe({
      next: () => {
        if (updated.includes('name')) {
          this.topToolbarService.setTitle(this.form.value.name, null);
        }
        this.siteGroups.refresh().then(() => {
          this.sites.refresh().then(() => this.sitesMap.refresh());
          this.siteGroupsMap.refresh();
        });
        this.basicService.toastSuccess('accounts/update_account_success');
      },
    });
  }

  uploadAccountPicture(event) {
    const file = event.target.files[0];

    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      data: {
        description: this.basicService.translate('settings/account/profile/picture/changed/effective/delay'),
        cancelBtnText: this.basicService.translate('cancel'),
        confirmBtnText: this.basicService.translate('ok'),
      },
    });
    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.basicService.showSpinner();
        this.mediasService.uploadAccountProfilePicture2(
          this.alcmeonContextService.companyId,
          this.account.authorized_user.id,
          file,
          response => {
            this.basicService.hideSpinner();
            if (!this.account.authorized_user.details) {
              this.account.authorized_user.details = {
                has_profile_picture: true,
                'profile-name': null,
                avatarUrl: null,
              };
            }
            this.account.authorized_user.details.has_profile_picture = true;
            this.account.authorized_user.details.avatarUrl = response.data.details.avatarUrl;
          },
          error => {
            this.basicService.hideSpinner();
            this.basicService.toastAlert(error?.data?.error || 'common/http_error');
          }
        );
      }
    });
  }

  deletePicture() {
    from(this.mediasService.deleteAccountProfilePicture(this.$stateParams.companyId, this.account.authorized_user.id))
      .pipe(this.errorHandlingService.catchAlertAndThrowHttpError())
      .subscribe({
        next: () => {
          this.account.authorized_user.details.has_profile_picture = false;
        },
      });
  }

  generateQrCode(generateQRCodeTemplateRef: TemplateRef<GenerateQrCodeComponent>) {
    this.dialog.open(ConfirmDialogComponent, {
      panelClass: ['generate-qr-code'],
      data: {
        title: this.basicService.translate('accounts/edition/test-link/generate-qr-code/title'),
        cancelBtnText: 'close',
        templateRef: generateQRCodeTemplateRef,
      } as ConfirmDialogData,
    });
  }

  private getWABAAssociations(siteId: number) {
    this.basicService.showSpinner();
    this.whatsappTemplatesService
      .getWABAAssociations(siteId)
      .pipe(finalize(() => this.basicService.hideSpinner()))
      .subscribe({
        next: (wabaAssociations: WABAAssociation[]) => {
          (wabaAssociations || []).forEach(wabaAssociation =>
            this.wabaAssociationsMap.set(`${wabaAssociation.id}`, wabaAssociation)
          );
        },
      });
  }

  private getWhatsAppCloudAPIMessagingLimit() {
    this.basicService.showSpinner();
    this.whatsappCloudApiService
      .getMessagingLimit(this.account.id)
      .pipe(finalize(() => this.basicService.hideSpinner()))
      .subscribe({
        next: (whatsappCloudApiMessagingLimit: WhatsappCloudApiMessagingLimitEnum) => {
          this.whatsappCloudApiMessagingLimit = whatsappCloudApiMessagingLimit;
        },
      });
  }
}
